import axios, { AxiosInstance } from 'axios'
import type {
  AuthResponse,
  Invoice,
  Order,
  Shipment,
  Ticket,
  Document,
  Product,
  ProductVariant,
  Proposal,
  Project,
  AdminERP,
  AdminCustomer,
  AdminUser,
  UserPermissions,
  ActivityLog,
  AdminStats,
  UserGroup,
  Right,
  PaginatedResponse,
  RevisionRule,
  RevisionSchedule,
  EquipmentItem,
  EquipmentRevision,
  PAProvider,
  EInvoiceSubmission,
  EInvoiceSubmissionRow,
  User,
  Plan,
  CurrentSubscription,
} from '../types'

class ApiService {
  private api: AxiosInstance
  private token: string | null = null

  constructor() {
    // Restaurer le token depuis localStorage au démarrage
    this.token = localStorage.getItem('auth_token')

    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Cookie en fallback (same-origin / dev proxy)
    })

    // Intercepteur requête : ajouter le token Authorization + impersonation headers
    this.api.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`
      }
      // Client switcher (all_clients right)
      const impersonated = this.getImpersonatedCustomer()
      if (impersonated) {
        config.headers['X-As-Customer'] = String(impersonated)
      }
      // Super-admin ERP impersonation
      const asErpAdmin = this.getAsERPAdmin()
      if (asErpAdmin) {
        config.headers['X-As-ERP-Admin'] = String(asErpAdmin)
      }
      return config
    })

    // Intercepteur réponse : gérer les erreurs d'authentification
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || ''
        // Only clear auth for routes where a 401 means the session has truly expired.
        // Non-critical routes (notifications bell) should not log the user out.
        const isNonCritical = url.includes('/notifications')
        if (error.response?.status === 401 && !url.includes('/auth/login') && !isNonCritical) {
          this.clearAuth()
          // Pas de window.location.href ici : PrivateRoute gère la redirection
        }
        return Promise.reject(error)
      }
    )
  }

  // Gestion du token d'authentification
  private setAuth(token: string, user: User) {
    this.token = token
    localStorage.setItem('auth_token', token)
    localStorage.setItem('user', JSON.stringify(user))
    window.dispatchEvent(new Event('userChanged'))
  }

  private clearAuth() {
    this.token = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    window.dispatchEvent(new Event('userChanged'))
    // Always clear impersonation on auth reset (logout, session expiry)
    localStorage.removeItem('impersonated_customer')
    localStorage.removeItem('impersonated_customer_name')
    localStorage.removeItem('as_erp_admin_id')
    localStorage.removeItem('as_erp_admin_name')
  }

  // Authentification
  async login(email: string, password: string): Promise<AuthResponse> {
    const { data } = await this.api.post<AuthResponse>('/auth/login', { email, password })
    // Clear any leftover impersonation from a previous session
    localStorage.removeItem('impersonated_customer')
    localStorage.removeItem('impersonated_customer_name')
    // Stocker le token pour les requêtes suivantes (Authorization header)
    if (data.token) {
      this.setAuth(data.token, data.user)
    } else {
      // Fallback : stocker uniquement l'utilisateur (cookie seul)
      localStorage.setItem('user', JSON.stringify(data.user))
    }
    return data
  }

  // Returns the list of active ERPs (public endpoint — for registration autocomplete)
  async getPublicERPs(): Promise<{ id: number; name: string; siren: string | null }[]> {
    const { data } = await this.api.get('/auth/erps')
    return data
  }

  // Returns supplier branding (name, logo, website) by SIREN — public endpoint for white-label login
  async getERPBrandBySiren(siren: string): Promise<{
    siren: string
    erp_name: string
    logo: string | null
    website: string | null
    shop_enabled: boolean
    shop_show_prices: boolean
  }> {
    const { data } = await this.api.get(`/auth/erp-brand/${encodeURIComponent(siren)}`)
    return data
  }

  // Returns paginated products for the public shop (no JWT required) — identified by supplier SIREN
  async getPublicShopProducts(
    siren: string,
    filters?: {
      page?: number
      limit?: number
      search?: string
      category?: string
    }
  ): Promise<{
    data: Product[]
    pagination: { total: number; page: number; limit: number; pages: number }
  }> {
    const { data } = await this.api.get(`/public/shop/${encodeURIComponent(siren)}/products`, {
      params: filters,
    })
    return data
  }

  /** AI shop assistant — natural-language product search in the public shop (no auth) */
  async aiSearchPublicShop(
    siren: string,
    query: string
  ): Promise<{
    data: Product[]
    keywords: string[]
    total: number
  }> {
    const { data } = await this.api.post(`/public/shop/${encodeURIComponent(siren)}/ai-search`, {
      query,
    })
    return data
  }

  // Returns product categories for the public shop (no auth required)
  async getPublicShopCategories(siren: string): Promise<Array<{ id: string; label: string }>> {
    const { data } = await this.api.get(`/public/shop/${encodeURIComponent(siren)}/categories`)
    return data
  }

  // Builds the public image URL for a product in the shop (no token required)
  getPublicShopImageUrl(siren: string, productId: string): string {
    const apiBase = import.meta.env.VITE_API_URL || '/api'
    return `${apiBase}/public/shop/${encodeURIComponent(siren)}/products/${encodeURIComponent(productId)}/image`
  }

  // Submit a guest (unauthenticated) order from the public shop
  async submitPublicShopOrder(
    siren: string,
    payload: {
      firstname: string
      lastname: string
      email: string
      phone?: string
      company_name?: string
      siren?: string
      notes?: string
      cart: Array<{
        product_id: string
        ref: string
        label: string
        quantity: number
        unit_price: number
        price_ttc: number
      }>
    }
  ): Promise<{ success: boolean; id: number }> {
    const { data } = await this.api.post(`/public/shop/${encodeURIComponent(siren)}/order`, payload)
    return data
  }

  async registerRequest(
    data: Record<string, unknown>
  ): Promise<{ success: boolean; message: string }> {
    const { data: result } = await this.api.post('/auth/register-request', data)
    return result
  }

  async forgotPassword(email: string): Promise<void> {
    await this.api.post('/auth/forgot-password', { email })
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this.api.post('/auth/reset-password', { token, password })
  }

  async logout(): Promise<void> {
    // Appeler l'API pour supprimer le cookie côté serveur
    try {
      await this.api.post('/auth/logout')
    } catch {
      // Ignorer les erreurs de logout
    }
    this.clearAuth()
    const base = import.meta.env.BASE_URL || '/'
    window.location.href = base
  }

  getUser() {
    const userStr = localStorage.getItem('user')
    if (!userStr || userStr === 'undefined' || userStr === 'null') {
      localStorage.removeItem('user')
      return null
    }
    try {
      return JSON.parse(userStr)
    } catch {
      localStorage.removeItem('user')
      return null
    }
  }

  // Modules activés
  async getEnabledModules(): Promise<Record<string, boolean>> {
    const { data } = await this.api.get<Record<string, boolean>>('/config/modules')
    return data
  }

  // Profil
  async getProfile() {
    const { data } = await this.api.get('/me')
    return data
  }

  // ERP logo (base64) + supplier public website URL
  async getCustomerLogo(): Promise<{
    content: string | null
    mime_type: string | null
    website?: string | null
  } | null> {
    try {
      const { data } = await this.api.get('/me/logo')
      return data
    } catch {
      return null
    }
  }

  // Factures
  async getInvoices(params?: {
    page?: number
    limit?: number
  }): Promise<Invoice[] | PaginatedResponse<Invoice>> {
    const { data } = await this.api.get('/invoices', { params })
    return data
  }

  async getInvoice(id: string, erpId?: number): Promise<Invoice> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get<Invoice>(`/invoices/${id}`, { params })
    return data
  }

  async downloadInvoicePDF(id: string, erpId?: number): Promise<string> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get(`/invoices/${id}/download`, { params })
    return data.pdf // Le backend retourne {pdf: "base64string"}
  }

  // Commandes
  async getOrders(params?: {
    page?: number
    limit?: number
  }): Promise<Order[] | PaginatedResponse<Order>> {
    const { data } = await this.api.get('/orders', { params })
    return data
  }

  async getOrder(id: string, erpId?: number): Promise<Order> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get<Order>(`/orders/${id}`, { params })
    return data
  }

  async downloadOrderPDF(id: string, erpId?: number): Promise<string> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get(`/orders/${id}/download`, { params })
    return data.pdf // Le backend retourne {pdf: "base64string"}
  }

  // Expéditions
  async getShipments(params?: {
    page?: number
    limit?: number
  }): Promise<Shipment[] | PaginatedResponse<Shipment>> {
    const { data } = await this.api.get('/shipments', { params })
    return data
  }

  async getShipment(id: string, erpId?: number): Promise<Shipment> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get<Shipment>(`/shipments/${id}`, { params })
    return data
  }

  // Tickets
  async getTickets(params?: {
    page?: number
    limit?: number
  }): Promise<Ticket[] | PaginatedResponse<Ticket>> {
    const { data } = await this.api.get('/tickets', { params })
    return data
  }

  async getTicket(id: string): Promise<Ticket> {
    const { data } = await this.api.get<Ticket>(`/tickets/${id}`)
    return data
  }

  async createTicket(ticketData: {
    subject: string
    message: string
    type_code?: string
    category_code?: string
    severity_code?: string
  }): Promise<Ticket> {
    const { data } = await this.api.post<Ticket>('/tickets', ticketData)
    return data
  }

  async addTicketMessage(ticketId: string, message: string) {
    const { data } = await this.api.post(`/tickets/${ticketId}/messages`, { message })
    return data
  }

  // Upload multipart via fetch natif (bypass axios qui force Content-Type: application/json)
  private async fetchMultipart(url: string, formData: FormData): Promise<unknown> {
    const baseUrl = import.meta.env.VITE_API_URL || '/api'
    const headers: Record<string, string> = {}
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    const response = await fetch(`${baseUrl}${url}`, {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include', // Cookie en fallback
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Erreur HTTP ${response.status}`)
    }
    return response.json()
  }

  async uploadTicketAudio(
    ticketId: string,
    file: File
  ): Promise<{
    transcription: string
    message: Record<string, unknown>
    document: Record<string, unknown>
  }> {
    const formData = new FormData()
    formData.append('audio', file)
    return this.fetchMultipart(`/tickets/${ticketId}/audio`, formData) as Promise<{
      transcription: string
      message: Record<string, unknown>
      document: Record<string, unknown>
    }>
  }

  async createTicketWithAudio(ticketData: Record<string, string>, audioFile: File) {
    const formData = new FormData()
    Object.entries(ticketData).forEach(([key, value]) => formData.append(key, value))
    formData.append('audio', audioFile)
    return this.fetchMultipart('/tickets/create-with-audio', formData)
  }

  // Documents
  async getDocuments(filters?: { modulepart?: string }): Promise<Document[]> {
    const { data } = await this.api.get<Document[]>('/documents', {
      params: filters,
    })
    return data
  }

  // Galerie : tous les documents joints de tous les objets ERP du client
  async getGallery(filters?: { modulepart?: string }): Promise<Document[]> {
    const { data } = await this.api.get<Document[]>('/gallery', {
      params: filters,
    })
    return data
  }

  downloadDocument(fullPath: string): void {
    const baseUrl = import.meta.env.VITE_API_URL || '/api'
    // Add token as query param — window.open cannot send Authorization headers
    const tokenParam = this.token ? `&token=${encodeURIComponent(this.token)}` : ''
    const url = `${baseUrl}/documents/download?path=${encodeURIComponent(fullPath)}${tokenParam}`
    window.open(url, '_blank')
  }

  // Open a document inline in the browser (PDF, images) instead of forcing download
  viewDocument(fullPath: string): void {
    const baseUrl = import.meta.env.VITE_API_URL || '/api'
    const tokenParam = this.token ? `&token=${encodeURIComponent(this.token)}` : ''
    const url = `${baseUrl}/documents/download?path=${encodeURIComponent(fullPath)}&view=1${tokenParam}`
    window.open(url, '_blank')
  }

  // Produits (Catalogue) — returns paginated response
  async getProducts(filters?: {
    page?: number
    limit?: number
    category?: string
    recently_purchased?: number
    search?: string
  }): Promise<{
    data: Product[]
    pagination: { total: number; page: number; limit: number; pages: number }
  }> {
    const { data } = await this.api.get('/products', { params: filters })
    return data
  }

  /** AI shop assistant — natural-language product search (authenticated catalog) */
  async aiSearchProducts(query: string): Promise<{
    data: Product[]
    keywords: string[]
    total: number
  }> {
    const { data } = await this.api.post('/products/ai-search', { query })
    return data
  }

  async getProduct(id: string): Promise<Product> {
    const { data } = await this.api.get<Product>(`/products/${id}`)
    return data
  }

  // Création de commande
  async createOrder(orderData: {
    lines: Array<{
      product_id: string
      quantity: number
      unit_price?: number
      description?: string
    }>
    notes?: string
  }): Promise<Order> {
    const { data } = await this.api.post<Order>('/orders/create', orderData)
    return data
  }

  // Devis / Propositions commerciales
  async getProposals(params?: {
    page?: number
    limit?: number
  }): Promise<Proposal[] | PaginatedResponse<Proposal>> {
    const { data } = await this.api.get('/proposals', { params })
    return data
  }

  async getProposal(id: string, erpId?: number): Promise<Proposal> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get<Proposal>(`/proposals/${id}`, { params })
    return data
  }

  async getProjects(params?: {
    page?: number
    limit?: number
  }): Promise<Project[] | PaginatedResponse<Project>> {
    const { data } = await this.api.get('/projects', { params })
    return data
  }

  async getProject(id: string, erpId?: number): Promise<Project> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get<Project>(`/projects/${id}`, { params })
    return data
  }

  async downloadProposalPDF(id: string, erpId?: number): Promise<string> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get(`/proposals/${id}/download`, { params })
    return data.pdf
  }

  async downloadProposalSignedPDF(id: string, erpId?: number): Promise<string> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get(`/proposals/${id}/signed-document`, { params })
    return data.pdf
  }

  async signProposal(
    id: string,
    locale?: string
  ): Promise<{ request_id: string; signing_url: string; status: string; provider: string }> {
    const { data } = await this.api.post(`/proposals/${id}/sign`, { locale: locale || 'fr' })
    return data
  }

  async getProposalSignStatus(id: string): Promise<{ status: string; signed_at?: string }> {
    const { data } = await this.api.get(`/proposals/${id}/sign-status`)
    return data
  }

  // Generates a Firma JWT for the embedded signing editor (called when customer clicks "Signer maintenant")
  async getProposalSignEmbedToken(proposalId: string): Promise<{
    token: string
    signing_request_id: string
    signing_url?: string
    provider: string
  }> {
    const { data } = await this.api.get(`/proposals/${proposalId}/sign-embed-token`)
    return data
  }

  // Admin: send a proposal for e-signature — contact fields are required
  async adminSendProposalForSignature(
    proposalId: string,
    customerId?: number,
    contactEmail?: string,
    contactFirstname?: string,
    contactLastname?: string,
    locale?: string
  ): Promise<{ success: boolean; request_id: string; signing_url: string; provider: string }> {
    const { data } = await this.api.post('/admin/proposals/send-for-signature', {
      proposal_id: proposalId,
      customer_id: customerId,
      contact_email: contactEmail,
      contact_firstname: contactFirstname,
      contact_lastname: contactLastname,
      locale: locale || 'fr',
    })
    return data
  }

  // Admin: list contacts linked to a proposal in the ERP (for recipient selection)
  async getProposalContacts(
    proposalId: string,
    customerId?: number
  ): Promise<
    Array<{
      id: string
      email: string
      firstname: string
      lastname: string
      fullname: string
      phone: string
      poste: string
    }>
  > {
    const params: Record<string, unknown> = {}
    if (customerId) params.customer_id = customerId
    const { data } = await this.api.get(`/admin/proposals/${proposalId}/contacts`, { params })
    return data
  }

  // Admin: list proposals for a specific customer, enriched with their signature status
  async getAdminProposals(params: {
    customer_id: number
    page?: number
    limit?: number
  }): Promise<
    PaginatedResponse<
      Proposal & { signature_status: string; signing_url?: string; request_id?: string }
    >
  > {
    const { data } = await this.api.get('/admin/proposals', { params })
    return data
  }

  // ==================== Document Signatures ====================

  async getDocumentSignatures(params?: {
    limit?: number
    offset?: number
  }): Promise<{ data: import('../types').DocumentSignature[]; total: number }> {
    const { data } = await this.api.get('/document-signatures', { params })
    return data
  }

  async getDocumentSignature(id: number): Promise<import('../types').DocumentSignature> {
    const { data } = await this.api.get(`/document-signatures/${id}`)
    return data
  }

  async createDocumentSignature(payload: {
    document_name: string
    pdf_base64: string
    signers: Array<{ email: string; first_name: string; last_name: string }>
    locale?: string
  }): Promise<{
    id: number
    request_id: string
    signing_url: string
    status: string
    provider: string
  }> {
    const { data } = await this.api.post('/document-signatures', payload)
    return data
  }

  async getDocumentSignatureContacts(): Promise<
    Array<{
      id: string
      email: string
      firstname: string
      lastname: string
      fullname: string
      phone: string
      poste: string
    }>
  > {
    const { data } = await this.api.get('/document-signatures/contacts')
    return data
  }

  // Admin: get contacts for a specific customer (no X-As-Customer needed)
  async getAdminDocumentSignatureContacts(customerId: number): Promise<
    Array<{
      id: string
      email: string
      firstname: string
      lastname: string
      fullname: string
      phone: string
      poste: string
    }>
  > {
    const { data } = await this.api.get('/document-signatures/contacts', {
      params: { customer_id: customerId },
    })
    return data
  }

  // Admin: list document signatures for a specific customer
  async getAdminDocumentSignatures(params: {
    customer_id: number
    limit?: number
    offset?: number
  }): Promise<{ data: import('../types').DocumentSignature[]; total: number }> {
    const { data } = await this.api.get('/document-signatures', { params })
    return data
  }

  // Admin: create a document signature for a specific customer
  async createAdminDocumentSignature(payload: {
    document_name: string
    pdf_base64: string
    signers: Array<{ email: string; first_name: string; last_name: string }>
    locale?: string
    customer_id: number
  }): Promise<{
    id: number
    request_id: string
    signing_url: string
    status: string
    provider: string
  }> {
    const { data } = await this.api.post('/document-signatures', payload)
    return data
  }

  async getDocumentSignatureEmbedToken(id: number): Promise<{
    token: string
    signing_request_id: string
    signing_url: string | null
    provider: string
  }> {
    const { data } = await this.api.get(`/document-signatures/${id}/embed-token`)
    return data
  }

  async downloadDocumentSignature(id: number): Promise<{ pdf: string; document_name: string }> {
    const { data } = await this.api.get(`/document-signatures/${id}/download`)
    return data
  }

  // Documents d'un produit
  async getProductDocuments(productId: string): Promise<Document[]> {
    const { data } = await this.api.get<Document[]>(`/products/${productId}/documents`)
    return data
  }

  // Variantes d'un produit (Dolibarr only, others return [])
  async getProductVariants(productId: string): Promise<ProductVariant[]> {
    const { data } = await this.api.get<ProductVariant[]>(`/products/${productId}/variants`)
    return data
  }

  // ==================== Admin ====================

  // Admin Stats
  async getAdminStats(): Promise<AdminStats> {
    const { data } = await this.api.get<AdminStats>('/admin/stats')
    return data
  }

  // Aide contextuelle ID client par type d'ERP
  async getCustomerIdHelps(): Promise<Record<string, { placeholder: string; help: string }>> {
    const { data } = await this.api.get<Record<string, { placeholder: string; help: string }>>(
      '/admin/erp-customer-id-help'
    )
    return data
  }

  // Admin ERPs
  async getAdminERPs(): Promise<AdminERP[]> {
    const { data } = await this.api.get<AdminERP[]>('/admin/erps')
    return data
  }

  async createAdminERP(erpData: Partial<AdminERP>): Promise<AdminERP> {
    const { data } = await this.api.post<AdminERP>('/admin/erps', erpData)
    return data
  }

  async updateAdminERP(id: number, erpData: Partial<AdminERP>): Promise<AdminERP> {
    const { data } = await this.api.put<AdminERP>(`/admin/erps/${id}`, erpData)
    return data
  }

  async deleteAdminERP(id: number): Promise<void> {
    await this.api.delete(`/admin/erps/${id}`)
  }

  async testERPConnection(id: number): Promise<{
    success: boolean
    message?: string
    modules?: Record<string, { success: boolean; count?: number; error?: string }>
  }> {
    const { data } = await this.api.post(`/admin/erps/${id}/test`)
    return data
  }

  async testPAConnection(
    id: number,
    provider?: string,
    config?: Record<string, string>
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.post(`/admin/erps/${id}/test-pa`, {
      pa_provider: provider,
      pa_config: config,
    })
    return data
  }

  // Admin ERP Modules
  async getERPModules(
    erpId: number
  ): Promise<Record<string, { is_active: boolean; is_enabled: boolean }>> {
    const { data } = await this.api.get<
      Record<string, { is_active: boolean; is_enabled: boolean }>
    >(`/admin/erps/${erpId}/modules`)
    return data
  }

  async updateERPModules(
    erpId: number,
    modules: Record<string, { is_active?: boolean; is_enabled: boolean }>
  ): Promise<Record<string, { is_active: boolean; is_enabled: boolean }>> {
    const { data } = await this.api.put<
      Record<string, { is_active: boolean; is_enabled: boolean }>
    >(`/admin/erps/${erpId}/modules`, modules)
    return data
  }

  // PA Providers
  async getPAProviders(): Promise<PAProvider[]> {
    const { data } = await this.api.get<PAProvider[]>('/admin/pa-providers')
    return data
  }

  // E-invoice submission
  async submitInvoiceToPA(invoiceId: string, erpId?: number): Promise<EInvoiceSubmission> {
    const params = erpId ? { erp_id: erpId } : {}
    const { data } = await this.api.post<EInvoiceSubmission>(
      `/invoices/${invoiceId}/submit-to-pa`,
      {},
      { params }
    )
    return data
  }

  async getInvoiceSubmissions(invoiceId: string, erpId?: number): Promise<EInvoiceSubmissionRow[]> {
    const params = erpId ? { erp_id: erpId } : {}
    const { data } = await this.api.get<EInvoiceSubmissionRow[]>(
      `/invoices/${invoiceId}/submissions`,
      { params }
    )
    return data
  }

  // Admin Customers
  async getAdminCustomers(filters?: { erp_id?: number }): Promise<AdminCustomer[]> {
    const { data } = await this.api.get<AdminCustomer[]>('/admin/customers', { params: filters })
    return data
  }

  async createAdminCustomer(customerData: Partial<AdminCustomer>): Promise<AdminCustomer> {
    const { data } = await this.api.post<AdminCustomer>('/admin/customers', customerData)
    return data
  }

  async updateAdminCustomer(
    id: number,
    customerData: Partial<AdminCustomer>
  ): Promise<AdminCustomer> {
    const { data } = await this.api.put<AdminCustomer>(`/admin/customers/${id}`, customerData)
    return data
  }

  async deleteAdminCustomer(id: number): Promise<void> {
    await this.api.delete(`/admin/customers/${id}`)
  }

  async syncPreviewCustomers(
    erpId: number,
    limit: number,
    filters?: {
      hasInvoices?: boolean
      invoicesMonths?: number
      hasProposals?: boolean
      proposalsMonths?: number
    }
  ): Promise<{
    candidates: Array<{
      erp_customer_id: string
      customer_name: string
      city: string | null
      email: string | null
    }>
    total_in_erp: number
    already_synced: number
  }> {
    const params: Record<string, unknown> = { erp_id: erpId, limit }
    if (filters?.hasInvoices) {
      params.has_invoices = 1
      params.invoices_months = filters.invoicesMonths ?? 6
    }
    if (filters?.hasProposals) {
      params.has_proposals = 1
      params.proposals_months = filters.proposalsMonths ?? 6
    }
    const { data } = await this.api.get('/admin/customers/sync-preview', { params })
    return data
  }

  async syncImportCustomers(
    erpId: number,
    customers: Array<{
      erp_customer_id: string
      customer_name: string
      city?: string | null
      email?: string | null
    }>
  ): Promise<{ imported: number; skipped: number }> {
    const { data } = await this.api.post('/admin/customers/sync', { erp_id: erpId, customers })
    return data
  }

  async importCustomersCSV(
    erpId: number,
    customers: Record<string, string>[]
  ): Promise<{ created: number; updated: number; errors: { line: number; message: string }[] }> {
    const { data } = await this.api.post('/admin/import/customers', { erp_id: erpId, customers })
    return data
  }

  async searchErpCustomers(
    erpId: number,
    q: string
  ): Promise<Array<{ erp_customer_id: string; customer_name: string; city: string | null }>> {
    const { data } = await this.api.get('/admin/customers/search', { params: { erp_id: erpId, q } })
    return data
  }

  // Admin Users
  async getAdminUsers(): Promise<AdminUser[]> {
    const { data } = await this.api.get<AdminUser[]>('/admin/users')
    return data
  }

  async createAdminUser(userData: Partial<AdminUser> & { password?: string }): Promise<AdminUser> {
    const { data } = await this.api.post<AdminUser>('/admin/users', userData)
    return data
  }

  async updateAdminUser(id: number, userData: Partial<AdminUser>): Promise<AdminUser> {
    const { data } = await this.api.put<AdminUser>(`/admin/users/${id}`, userData)
    return data
  }

  async deleteAdminUser(id: number): Promise<void> {
    await this.api.delete(`/admin/users/${id}`)
  }

  async resetAdminUserPassword(id: number): Promise<{ password: string }> {
    const { data } = await this.api.post<{ password: string }>(`/admin/users/${id}/reset-password`)
    return data
  }

  async sendAdminUserResetEmail(id: number): Promise<void> {
    await this.api.post(`/admin/users/${id}/send-reset-email`)
  }

  async getUserPermissions(id: number): Promise<UserPermissions> {
    const { data } = await this.api.get<UserPermissions>(`/admin/users/${id}/permissions`)
    return data
  }

  async deleteAdminUsers(ids: number[]): Promise<void> {
    await Promise.all(ids.map((id) => this.api.delete(`/admin/users/${id}`)))
  }

  // Admin Registration Requests
  async getAdminRegistrationRequests(params?: Record<string, unknown>): Promise<{
    data: {
      id: number
      request_type: 'supplier' | 'client'
      company_name: string
      siren: string
      lastname: string
      firstname: string
      email: string
      phone: string
      erp_type: string
      comment: string
      supplier_name?: string
      supplier_siren?: string
      supplier_erp_id?: number | null
      status: 'pending' | 'approved' | 'rejected'
      processed_by_email?: string
      processed_by_firstname?: string
      processed_by_lastname?: string
      processed_at?: string
      created_at: string
    }[]
    total: number
  }> {
    const { data } = await this.api.get('/admin/registration-requests', { params })
    return data
  }

  async approveRegistrationRequest(id: number): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.post(`/admin/registration-requests/${id}/approve`)
    return data
  }

  async rejectRegistrationRequest(
    id: number,
    reason?: string
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.post(`/admin/registration-requests/${id}/reject`, { reason })
    return data
  }

  // Admin Pending Orders (guest checkout)
  async getAdminPendingOrders(params?: {
    page?: number
    limit?: number
    status?: string
    erp_id?: number
  }): Promise<{
    data: {
      id: number
      erp_id: number
      erp_name: string
      firstname: string
      lastname: string
      email: string
      phone: string | null
      company_name: string | null
      siren: string | null
      cart: {
        product_id: string
        ref: string
        label: string
        quantity: number
        unit_price: number
        price_ttc: number
      }[]
      status: 'pending' | 'processed' | 'cancelled'
      notes: string | null
      created_at: string
      updated_at: string
    }[]
    total: number
    page: number
    limit: number
  }> {
    const { data } = await this.api.get('/admin/pending-orders', { params })
    return data
  }

  async updatePendingOrder(
    id: number,
    payload: { status?: string; notes?: string }
  ): Promise<{ success: boolean }> {
    const { data } = await this.api.put(`/admin/pending-orders/${id}`, payload)
    return data
  }

  async deletePendingOrder(id: number): Promise<{ success: boolean }> {
    const { data } = await this.api.delete(`/admin/pending-orders/${id}`)
    return data
  }

  async processAdminPendingOrder(
    id: number,
    payload:
      | { erp_customer_id: string; create_customer?: false }
      | { create_customer: true; customer_name?: string }
  ): Promise<{
    success: boolean
    order_id: string | null
    order_ref: string
    erp_customer_id: string
    customer_created: boolean
  }> {
    const { data } = await this.api.post(`/admin/pending-orders/${id}/process`, payload)
    return data
  }

  // Admin Activity Logs
  async getAdminActivityLogs(params?: {
    page?: number
    limit?: number
    user_id?: number
    action?: string
  }): Promise<{ data: ActivityLog[]; total: number }> {
    const { data } = await this.api.get<{ data: ActivityLog[]; total: number }>(
      '/admin/activity-logs',
      { params }
    )
    return data
  }

  // Admin Rights
  async getAdminRights(): Promise<Right[]> {
    const { data } = await this.api.get<Right[]>('/admin/rights')
    return data
  }

  // Admin Groups
  async getAdminGroups(): Promise<UserGroup[]> {
    const { data } = await this.api.get<UserGroup[]>('/admin/groups')
    return data
  }

  async createAdminGroup(groupData: Partial<UserGroup>): Promise<UserGroup> {
    const { data } = await this.api.post<UserGroup>('/admin/groups', groupData)
    return data
  }

  async updateAdminGroup(id: number, groupData: Partial<UserGroup>): Promise<UserGroup> {
    const { data } = await this.api.put<UserGroup>(`/admin/groups/${id}`, groupData)
    return data
  }

  async deleteAdminGroup(id: number): Promise<void> {
    await this.api.delete(`/admin/groups/${id}`)
  }

  async getAdminGroupMembers(groupId: number): Promise<AdminUser[]> {
    const { data } = await this.api.get<AdminUser[]>(`/admin/groups/${groupId}/members`)
    return data
  }

  async addAdminGroupMember(groupId: number, userId: number): Promise<void> {
    await this.api.post(`/admin/groups/${groupId}/members`, { user_id: userId })
  }

  async removeAdminGroupMember(groupId: number, userId: number): Promise<void> {
    await this.api.delete(`/admin/groups/${groupId}/members/${userId}`)
  }

  async getAdminGroupRights(groupId: number): Promise<Right[]> {
    const { data } = await this.api.get<Right[]>(`/admin/groups/${groupId}/rights`)
    return data
  }

  async updateAdminGroupRights(
    groupId: number,
    rightConfigs: Array<{
      right_id: number
      allowed_statuses: string[] | null
      access_level: 'read' | 'write'
    }>
  ): Promise<void> {
    await this.api.put(`/admin/groups/${groupId}/rights`, { right_configs: rightConfigs })
  }

  // ==================== Super-admin ERP impersonation ====================

  /** Store the ERP ID the super-admin is impersonating as admin */
  setAsERPAdmin(erpId: number, erpName: string): void {
    localStorage.setItem('as_erp_admin_id', String(erpId))
    localStorage.setItem('as_erp_admin_name', erpName)
  }

  /** Get current ERP ID being impersonated as admin (null if not impersonating) */
  getAsERPAdmin(): string | null {
    return localStorage.getItem('as_erp_admin_id')
  }

  /** Get current ERP name being impersonated as admin */
  getAsERPAdminName(): string | null {
    return localStorage.getItem('as_erp_admin_name')
  }

  /** Clear the ERP admin impersonation */
  clearAsERPAdmin(): void {
    localStorage.removeItem('as_erp_admin_id')
    localStorage.removeItem('as_erp_admin_name')
  }

  // ==================== Client switcher (all_clients right) ====================

  /** Store the impersonated customer's ERP ID in localStorage */
  setImpersonatedCustomer(erpCustomerId: string): void {
    localStorage.setItem('impersonated_customer', erpCustomerId)
  }

  /** Get current impersonated customer ERP ID (null if not impersonating) */
  getImpersonatedCustomer(): string | null {
    return localStorage.getItem('impersonated_customer')
  }

  /** Clear the impersonated supplier context */
  clearImpersonatedCustomer(): void {
    localStorage.removeItem('impersonated_customer')
    localStorage.removeItem('impersonated_customer_name')
  }

  // ── Revision schedules (client) ─────────────────────────────────────────

  /** List revision schedules for the authenticated supplier */
  async getRevisions(): Promise<RevisionSchedule[]> {
    const { data } = await this.api.get('/revisions')
    return data
  }

  /** Acknowledge a revision (mark as done) */
  async acknowledgeRevision(id: number): Promise<RevisionSchedule> {
    const { data } = await this.api.post(`/revisions/${id}/acknowledge`)
    return data
  }

  // ── Revision rules + schedules (admin) ──────────────────────────────────

  /** List all revision rules */
  async getAdminRevisionRules(params?: Record<string, string>): Promise<RevisionRule[]> {
    const { data } = await this.api.get('/admin/revision-rules', { params })
    return data
  }

  /** Create a revision rule */
  async createAdminRevisionRule(payload: Partial<RevisionRule>): Promise<RevisionRule> {
    const { data } = await this.api.post('/admin/revision-rules', payload)
    return data
  }

  /** Update a revision rule */
  async updateAdminRevisionRule(id: number, payload: Partial<RevisionRule>): Promise<RevisionRule> {
    const { data } = await this.api.put(`/admin/revision-rules/${id}`, payload)
    return data
  }

  /** Delete a revision rule */
  async deleteAdminRevisionRule(id: number): Promise<void> {
    await this.api.delete(`/admin/revision-rules/${id}`)
  }

  /** List all revision schedules with optional filters */
  async getAdminRevisions(params?: Record<string, string>): Promise<RevisionSchedule[]> {
    const { data } = await this.api.get('/admin/revisions', { params })
    return data
  }

  /** Sync revision schedules from ERP invoices */
  async syncAdminRevisions(erpId?: number): Promise<{ synced: number; errors: string[] }> {
    const { data } = await this.api.post('/admin/revisions/sync', erpId ? { erp_id: erpId } : {})
    return data
  }

  // --- Customer team management (customer_admin only) ---

  /** List users in the same customer organisation */
  async getCustomerUsers(): Promise<AdminUser[]> {
    const { data } = await this.api.get('/customer/users')
    return data
  }

  /** Create (invite) a new user in the customer organisation */
  async createCustomerUser(payload: Partial<AdminUser>): Promise<AdminUser> {
    const { data } = await this.api.post('/customer/users', payload)
    return data
  }

  /** Update a user in the customer organisation */
  async updateCustomerUser(id: number, payload: Partial<AdminUser>): Promise<AdminUser> {
    const { data } = await this.api.put(`/customer/users/${id}`, payload)
    return data
  }

  /** Deactivate a user in the customer organisation */
  async deleteCustomerUser(id: number): Promise<void> {
    await this.api.delete(`/customer/users/${id}`)
  }

  // --- Equipment fleet (parc) — client routes ---

  /** List equipment items for the authenticated customer */
  async getParc(): Promise<EquipmentItem[]> {
    const { data } = await this.api.get('/parc')
    return data
  }

  /** Get revision history for one equipment item */
  async getParcItemHistory(id: number): Promise<EquipmentRevision[]> {
    const { data } = await this.api.get(`/parc/${id}/history`)
    return data
  }

  /** Log a revision event for an equipment item */
  async reviseEquipmentItem(id: number, revisedAt: string, notes?: string): Promise<EquipmentItem> {
    const { data } = await this.api.post(`/parc/${id}/revise`, { revised_at: revisedAt, notes })
    return data
  }

  // --- Equipment fleet (parc) — admin routes ---

  /** List all equipment items (admin) */
  async getAdminParc(params?: Record<string, string>): Promise<EquipmentItem[]> {
    const { data } = await this.api.get('/admin/parc', { params })
    return data
  }

  /** Create an equipment item (admin) */
  async createAdminParcItem(payload: Partial<EquipmentItem>): Promise<EquipmentItem> {
    const { data } = await this.api.post('/admin/parc', payload)
    return data
  }

  /** Update an equipment item (admin) */
  async updateAdminParcItem(id: number, payload: Partial<EquipmentItem>): Promise<EquipmentItem> {
    const { data } = await this.api.put(`/admin/parc/${id}`, payload)
    return data
  }

  /** Delete an equipment item (admin) */
  async deleteAdminParcItem(id: number): Promise<void> {
    await this.api.delete(`/admin/parc/${id}`)
  }

  /** Get revision history for one equipment item (admin) */
  async getAdminParcItemHistory(id: number): Promise<EquipmentRevision[]> {
    const { data } = await this.api.get(`/admin/parc/${id}/history`)
    return data
  }

  /** Log a revision event for an equipment item (admin) */
  async reviseAdminEquipmentItem(
    id: number,
    revisedAt: string,
    notes?: string
  ): Promise<EquipmentItem> {
    const { data } = await this.api.post(`/admin/parc/${id}/revise`, {
      revised_at: revisedAt,
      notes,
    })
    return data
  }

  /** Download a proposal PDF as base64 (admin access) */
  async downloadAdminProposalPDF(proposalId: string, customerId?: number): Promise<string> {
    const params = customerId ? { customer_id: customerId } : undefined
    const { data } = await this.api.get(`/admin/proposals/${proposalId}/download`, { params })
    return data.pdf
  }

  // ==================== Admin Products ====================

  /** List products for an ERP (admin access, bypasses catalog module check) */
  async getAdminProducts(params?: {
    erp_id?: number
    search?: string
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<Product>> {
    const { data } = await this.api.get<PaginatedResponse<Product>>('/admin/products', { params })
    return data
  }

  /** Get variants for a product (admin access) */
  async getAdminProductVariants(productId: string, erpId?: number): Promise<ProductVariant[]> {
    const params = erpId ? { erp_id: erpId } : undefined
    const { data } = await this.api.get<ProductVariant[]>(`/admin/products/${productId}/variants`, {
      params,
    })
    return data
  }

  // ==================== Chatbot ====================

  /**
   * Extract ticket fields from a free-text description using the AI backend.
   * Returns a map of question id → extracted value (null if not found).
   */
  async extractChatbotFields(
    description: string,
    questions: Array<{
      id: string
      label: string
      type: string
      options?: string[]
      required: boolean
      field: string
    }>
  ): Promise<Record<string, string | null>> {
    const { data } = await this.api.post('/chatbot/extract', { description, questions })
    return data
  }

  /** Get the chatbot configuration for the authenticated user's ERP */
  async getChatbotConfig(): Promise<{
    enabled: boolean
    welcome_message: string
    questions: Array<{
      id: string
      label: string
      type: 'text' | 'date' | 'select' | 'textarea'
      options?: string[]
      required: boolean
      field: 'subject' | 'message' | 'type_code' | 'category_code' | 'severity_code'
    }>
  }> {
    const { data } = await this.api.get('/chatbot/config')
    return data
  }

  /** Search suppliers by name/id/city/siren (requires all_clients right) */
  async searchClients(q: string): Promise<
    Array<{
      id: number
      customer_name: string
      erp_customer_id: string
      city: string | null
      erp_type: string
      erp_name: string
    }>
  > {
    const { data } = await this.api.get('/clients/search', { params: { q } })
    return data
  }

  // ==================== Vapi.ai ====================

  /** Get the Vapi voice bot config (enabled + phone number) for the current user's ERP */
  async getVapiConfig(): Promise<{ enabled: boolean; phone_number: string | null }> {
    const { data } = await this.api.get('/vapi/config')
    return data
  }

  /** List Vapi phone numbers available in the account (super-admin only) */
  async getVapiPhoneNumbers(): Promise<Array<{ id: string; number: string; name: string }>> {
    const { data } = await this.api.get('/admin/erps/vapi/phone-numbers')
    return data
  }

  /** Sync (create or update) the Vapi assistant for a given ERP */
  async syncVapiAssistant(
    erpId: number
  ): Promise<{ success: boolean; vapi_config: Record<string, unknown> }> {
    const { data } = await this.api.post(`/admin/erps/${erpId}/vapi/sync`)
    return data
  }

  // ── Usage counters ──────────────────────────────────────────────────────────

  async getUsageStats(params?: {
    months?: number
    erp_id?: number
  }): Promise<import('../types').UsageStats> {
    const { data } = await this.api.get('/admin/usage', { params })
    return data
  }

  // ── Stripe Connect config (admin) ───────────────────────────────────────────

  async getStripeConfig(erpId: number): Promise<import('../types').StripeConfig> {
    const { data } = await this.api.get(`/admin/erps/${erpId}/stripe`)
    return data
  }

  async saveStripeConfig(
    erpId: number,
    config: Partial<import('../types').StripeConfig>
  ): Promise<void> {
    await this.api.put(`/admin/erps/${erpId}/stripe`, config)
  }

  async getStripeAccountStatus(erpId: number): Promise<{
    status: 'not_connected' | 'pending' | 'active'
    charges_enabled?: boolean
    account_id?: string
  }> {
    const { data } = await this.api.get(`/admin/erps/${erpId}/stripe/account`)
    return data
  }

  async startStripeOnboarding(erpId: number): Promise<{ url: string; account_id: string }> {
    const { data } = await this.api.post(`/admin/erps/${erpId}/stripe/onboarding`)
    return data
  }

  async getStripeDashboardLink(erpId: number): Promise<{ url: string }> {
    const { data } = await this.api.get(`/admin/erps/${erpId}/stripe/dashboard`)
    return data
  }

  // ── Stripe platform publishable key (public) ─────────────────────────────────

  async getStripePlatformConfig(): Promise<{ publishable_key: string }> {
    const { data } = await this.api.get('/stripe/config')
    return data
  }

  // ── Stripe / Payment (client) ───────────────────────────────────────────────

  async createPaymentIntent(
    invoiceId: string
  ): Promise<{ client_secret: string; payment_intent_id: string; publishable_key: string }> {
    const { data } = await this.api.post(`/invoices/${invoiceId}/pay`)
    return data
  }

  async createSubscription(
    invoiceId: string
  ): Promise<{ subscription_id: string; client_secret: string; publishable_key: string }> {
    const { data } = await this.api.post(`/invoices/${invoiceId}/subscribe`)
    return data
  }

  async getInvoicePaymentStatus(
    invoiceId: string
  ): Promise<import('../types').InvoicePayment | null> {
    const { data } = await this.api.get(`/invoices/${invoiceId}/payment`)
    return data
  }

  async getMyPayments(): Promise<import('../types').InvoicePayment[]> {
    const { data } = await this.api.get('/payments')
    return Array.isArray(data) ? data : []
  }

  async getAdminPayments(params?: {
    erp_id?: number
    page?: number
    limit?: number
  }): Promise<import('../types').InvoicePayment[]> {
    const { data } = await this.api.get('/admin/payments', { params })
    return data
  }

  // ── Billing / Subscription plans (super admin) ──────────────────────────────

  async getBillingDashboard(): Promise<{
    success: boolean
    summary: Record<string, number>
    erps: {
      erp_id: number
      erp_name: string
      erp_type: string
      subscription: {
        plan_name: string
        status: string
        next_billing_date: string | null
        credit_balance: number
        monthly_allowance: number
        is_active: boolean
        is_custom: boolean
        quantity: number
        auto_recharge_enabled: boolean
        effective_max_users: number
        effective_max_clients: number
        effective_max_signatures: number
        effective_max_ai_tokens: number
        effective_max_emails: number
      }
    }[]
  }> {
    const { data } = await this.api.get('/admin/billing/dashboard')
    return data
  }

  async getSubscriptionPlans(): Promise<{ success: boolean; plans: Plan[] }> {
    const { data } = await this.api.get('/admin/billing/plans')
    return data
  }

  async saveSubscriptionPlan(
    plan: Partial<Plan>
  ): Promise<{ success: boolean; message: string; plan_id?: number }> {
    const { data } = await this.api.post('/admin/billing/plans', plan)
    return data
  }

  async deleteSubscriptionPlan(planId: number): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.delete(`/admin/billing/plans/${planId}`)
    return data
  }

  async getCreditCosts(): Promise<{
    success: boolean
    costs: {
      resource_type: string
      description: string
      credits_per_unit: number
      units_per_credit: number
      is_active: boolean
    }[]
  }> {
    const { data } = await this.api.get('/admin/billing/credit-costs')
    return data
  }

  async saveCreditCosts(
    costs: {
      resource_type: string
      description: string
      credits_per_unit: number
      units_per_credit: number
      is_active: boolean
    }[]
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.post('/admin/billing/credit-costs', { costs })
    return data
  }

  async activateErpSubscription(
    erpId: number,
    planId: number,
    quantity: number = 1
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.post('/admin/billing/activate-subscription', {
      erp_id: erpId,
      plan_id: planId,
      quantity,
    })
    return data
  }

  async addErpCredits(
    erpId: number,
    amount: number,
    description?: string
  ): Promise<{ success: boolean; message: string; new_balance?: number }> {
    const { data } = await this.api.post('/admin/billing/add-credits', {
      erp_id: erpId,
      amount,
      description,
    })
    return data
  }

  async getErpSubscription(
    erpId: number
  ): Promise<{ success: boolean; subscription: Record<string, unknown> }> {
    const { data } = await this.api.get('/admin/billing/subscription', {
      params: { erp_id: erpId },
    })
    return data
  }

  async getErpCredits(erpId: number): Promise<{
    success: boolean
    credit_pool: Record<string, unknown>
    monthly_usage: Record<string, unknown>
  }> {
    const { data } = await this.api.get('/admin/billing/credits', { params: { erp_id: erpId } })
    return data
  }

  async updateAutoRecharge(
    erpId: number,
    settings: { enabled: boolean; threshold: number; amount: number }
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.post('/admin/billing/auto-recharge', {
      erp_id: erpId,
      ...settings,
    })
    return data
  }

  // Public pricing plans — no auth needed (landing page)
  async getPublicPricingPlans(): Promise<Plan[]> {
    try {
      const { data } = await this.api.get('/public/plans')
      return data.plans ?? []
    } catch {
      return []
    }
  }

  // Self-service billing (ERP admin — own ERP only)
  async getPublicPlans(): Promise<{ success: boolean; plans: Plan[] }> {
    const { data } = await this.api.get('/billing/plans')
    return data
  }

  async getMySubscription(): Promise<{
    success: boolean
    subscription?: CurrentSubscription
    erp_name?: string
  }> {
    const { data } = await this.api.get('/billing/my-subscription')
    return data
  }

  async getSubscriptionProration(
    planId: number,
    quantity: number = 1
  ): Promise<{
    proration: {
      current_plan_name: string
      current_price_ht: number
      new_plan_name: string
      new_price_ht: number
      period_start: string
      next_billing_date: string
      total_period_days: number
      remaining_days: number
      prorated_credit_ht: number
      amount_due_ht: number
      vat_rate: number
      amount_due_ttc: number
      requires_payment: boolean
    } | null
    requires_payment?: boolean
  }> {
    const { data } = await this.api.get('/billing/subscribe/proration', {
      params: { plan_id: planId, quantity },
    })
    return data
  }

  async confirmSubscriptionChange(
    paymentIntentId: string,
    planId: number,
    quantity: number
  ): Promise<{ success: boolean; subscription?: CurrentSubscription }> {
    const { data } = await this.api.post('/billing/subscribe/confirm', {
      payment_intent_id: paymentIntentId,
      plan_id: planId,
      quantity,
    })
    return data
  }

  async subscribeToPlan(
    planId: number,
    quantity: number = 1
  ): Promise<{
    success: boolean
    message?: string
    subscription?: CurrentSubscription
    requires_payment?: boolean
    client_secret?: string
    publishable_key?: string
    payment_intent_id?: string
    proration?: Record<string, unknown>
  }> {
    const { data } = await this.api.post('/billing/subscribe', { plan_id: planId, quantity })
    return data
  }

  // Credit purchase self-service (ERP admin)
  async getCreditsDashboard(): Promise<{
    pool: { available_credits?: number }
    quota_status: {
      plan_name: string
      usage: { esignature?: number; email?: number; ai_tokens?: number }
      quotas: { max_signatures?: number; max_emails?: number; max_ai_tokens?: number }
      warnings?: string[]
    } | null
    transactions: { id: string | number; description: string; amount: number; created_at: string }[]
    last_purchase: number
    threshold_20pct?: number
    publishable_key: string
  }> {
    const { data } = await this.api.get('/billing/credits/dashboard')
    return data
  }

  async initiateCreditsPurchase(amountEur: number): Promise<{
    success: boolean
    client_secret: string
    publishable_key: string
    payment_intent_id: string
    amount_eur: number
    amount_ht: number
    vat_rate: number
    amount_ttc: number
  }> {
    const { data } = await this.api.post('/billing/credits/purchase', { amount_eur: amountEur })
    return data
  }

  async confirmCreditsPurchase(paymentIntentId: string): Promise<{
    success: boolean
    amount_eur: number
    new_balance: number
    message: string
  }> {
    const { data } = await this.api.post('/billing/credits/confirm', {
      payment_intent_id: paymentIntentId,
    })
    return data
  }

  // ── Supplier profile (ERP admin) ─────────────────────────────────────────────

  async getAdminProfile(): Promise<{
    erp_name: string
    erp_description: string | null
    siren: string | null
    website: string | null
    address: string | null
    postal_code: string | null
    city: string | null
    country_code: string | null
    contact_email: string | null
    contact_phone: string | null
  }> {
    const { data } = await this.api.get('/admin/profile')
    return data
  }

  async updateAdminProfile(
    profile: Partial<{
      erp_name: string
      erp_description: string
      siren: string
      website: string
      address: string
      postal_code: string
      city: string
      country_code: string
      contact_email: string
      contact_phone: string
    }>
  ): Promise<{ success: boolean }> {
    const { data } = await this.api.put('/admin/profile', profile)
    return data
  }

  // ── Super admin personal account ─────────────────────────────────────────────

  async getAccount(): Promise<{
    email: string
    first_name: string
    last_name: string
  }> {
    const { data } = await this.api.get('/me')
    // /me returns { user: {...}, erp_info: {...}, customer_info: {...} }
    const u = data.user ?? data
    return { email: u.email, first_name: u.first_name, last_name: u.last_name }
  }

  async updateAccount(
    account: Partial<{
      email: string
      first_name: string
      last_name: string
      new_password: string
    }>
  ): Promise<{ success: boolean }> {
    const { data } = await this.api.put('/me', account)
    // Refresh cached user so getUser() stays in sync
    if (data.email !== undefined) {
      const current = this.getUser() ?? {}
      const updated = {
        ...current,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
      }
      localStorage.setItem('user', JSON.stringify(updated))
      window.dispatchEvent(new Event('userChanged'))
    }
    return data
  }

  // ---- Migration / CSV Export-Import ----

  async getMigrationModules(): Promise<{
    success: boolean
    modules: Record<string, { label: string; importable: boolean; description: string }>
  }> {
    const { data } = await this.api.get('/admin/migration/modules')
    return data
  }

  async downloadMigrationTemplate(module: string): Promise<void> {
    const response = await this.api.get('/admin/migration/template', {
      params: { module },
      responseType: 'blob',
    })
    const url = URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `template_${module}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  async downloadMigrationExport(erpId: number, module: string): Promise<void> {
    const response = await this.api.get('/admin/migration/export', {
      params: { erp_id: erpId, module },
      responseType: 'blob',
    })
    const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-')
    const url = URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `export_${module}_erp${erpId}_${ts}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  async importMigrationCsv(
    erpId: number,
    module: string,
    file: File
  ): Promise<{
    success: boolean
    created: number
    updated: number
    skipped: number
    total_rows: number
    errors: string[]
  }> {
    const form = new FormData()
    form.append('erp_id', String(erpId))
    form.append('module', module)
    form.append('file', file)
    const { data } = await this.api.post('/admin/migration/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  }

  // ── In-app notifications (bell) ─────────────────────────────────────────────

  async getNotifications(offset = 0, limit = 10): Promise<import('../types').AppNotification[]> {
    const { data } = await this.api.get('/notifications', { params: { offset, limit } })
    return data
  }

  async markNotificationsRead(id?: number): Promise<void> {
    await this.api.post('/notifications/mark-read', id ? { id } : {})
  }

  async deleteNotification(id: number): Promise<void> {
    await this.api.delete(`/notifications/${id}`)
  }

  // ── Notification rules ──────────────────────────────────────────────────────

  async getNotificationRules(erpId: number): Promise<import('../types').NotificationRule[]> {
    const { data } = await this.api.get('/admin/notification-rules', { params: { erp_id: erpId } })
    return data
  }

  async upsertNotificationRule(
    erpId: number,
    serviceType: string,
    rule: Partial<import('../types').NotificationRule>
  ): Promise<import('../types').NotificationRule> {
    const { data } = await this.api.put(`/admin/notification-rules/${serviceType}`, rule, {
      params: { erp_id: erpId },
    })
    return data
  }

  async triggerSignatureReminders(): Promise<{
    success: boolean
    results: { sent: number; skipped: number; failed: number }
  }> {
    const { data } = await this.api.post('/admin/notification-rules/trigger-signature-reminders')
    return data
  }

  // ── Invitation token (public, no auth) ──────────────────────────────────────

  async checkInvitation(token: string): Promise<{
    email: string
    firstname: string
    lastname: string
    customer_name: string
    erp_name: string
    proposal_id: string
  }> {
    const { data } = await this.api.get('/auth/check-invitation', { params: { token } })
    return data
  }

  async acceptInvitation(payload: {
    token: string
    password: string
    firstname: string
    lastname: string
  }): Promise<{ token: string; user: User; proposal_id: string }> {
    const { data } = await this.api.post('/auth/accept-invitation', payload)
    return data
  }

  // ── ERP webhook configuration ───────────────────────────────────────────────

  async getERPWebhookConfig(
    erpId: number
  ): Promise<{ webhook_url: string; secret: string; erp_id: number }> {
    const { data } = await this.api.get(`/admin/erps/${erpId}/webhook-config`)
    return data
  }

  async regenerateERPWebhookSecret(erpId: number): Promise<{ secret: string; warning: string }> {
    const { data } = await this.api.post(`/admin/erps/${erpId}/webhook-secret/regenerate`)
    return data
  }

  // Public subscription flow — Step 0: save registration for cart-abandonment tracking
  async savePublicRegistration(payload: {
    plan_id: number
    company_name: string
    siren?: string
    lastname: string
    firstname: string
    email: string
    phone?: string
    erp_type: string
    comment?: string
  }): Promise<{ success: boolean; request_id: number }> {
    const { data } = await this.api.post('/public/subscription/save', payload)
    return data
  }

  // Public subscription flow — Step 1: create PaymentIntent (optionally update existing draft)
  async initiatePublicSubscription(payload: {
    plan_id: number
    quantity: number
    request_id?: number
    company_name: string
    siren?: string
    lastname: string
    firstname: string
    email: string
    phone?: string
    erp_type: string
    comment?: string
  }): Promise<{
    success: boolean
    client_secret: string
    publishable_key: string
    request_id: number
    amount_eur: number
    plan_name: string
    amount_ht: number
    vat_rate: number
    amount_ttc: number
  }> {
    const { data } = await this.api.post('/public/subscription/request', payload)
    return data
  }

  // Public subscription flow — Step 2: confirm payment status
  async confirmPublicSubscription(
    requestId: number,
    paymentIntentId: string
  ): Promise<{ success: boolean; payment_status: string; message: string }> {
    const { data } = await this.api.post('/public/subscription/confirm', {
      request_id: requestId,
      payment_intent_id: paymentIntentId,
    })
    return data
  }
}

export default new ApiService()
